import {Component, computed, inject, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatOptionModule} from "@angular/material/core"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatSelectModule} from "@angular/material/select"
import {ContentTypeModel, FabricOverviewGenerateTemplateType, MutationUpdateTagInput, TagType} from "@generated"
import {TagDetailsItemFragment} from "@app/platform/components/tags/tag-details/tag-details.generated"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NativeInputTextAreaComponent} from "@common/components/inputs/native/native-input-text-area/native-input-text-area.component"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {ColorInputComponent} from "@common/components/inputs/color-input/color-input.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {IsLoadingDirective} from "@common/directives"
import {TagAssignmentsComponent} from "@platform/components/tags/tag-assignments/tag-assignments.component"
import {GetTagDetailsGQL, UpdateTagDetailsGQL} from "@platform/components/tags/tag-details/tag-details.generated"
import {TagThumbnailComponent} from "@platform/components/tags/tag-thumbnail/tag-thumbnail.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {CheckboxComponent} from "@common/components/inputs/checkbox/checkbox.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DetailsDialogLayoutComponent,
        MatButtonModule,
        TagThumbnailComponent,
        InputContainerComponent,
        IsLoadingDirective,
        MatFormFieldModule,
        MatOptionModule,
        MatSelectModule,
        FormsModule,
        TagAssignmentsComponent,
        ColorInputComponent,
        NativeInputTextAreaComponent,
        StringInputComponent,
        SectionComponent,
        OrganizationSelectComponent,
        RoutedDialogComponent,
        TitleSectionComponent,
        NumericInputComponent,
        CheckboxComponent,
    ],
    selector: "cm-tag-details",
    styleUrl: "./tag-details.component.scss",
    templateUrl: "./tag-details.component.html",
})
export class TagDetailsComponent extends BaseDetailsComponent<TagDetailsItemFragment, Omit<MutationUpdateTagInput, "id">> {
    override _contentTypeModel = ContentTypeModel.Tag
    override _fetchItem = fetchThrowingErrors(inject(GetTagDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdateTagDetailsGQL))

    readonly organizations = inject(OrganizationsService)

    readonly $tagTypes = computed(() => Array.from(this.Labels.TagType.values()).filter((label) => this.$can().read.tagType(label.state)))

    protected readonly DialogSize = DialogSize
    protected readonly TagType = TagType
    protected readonly FabricOverviewGenerateTemplateType = FabricOverviewGenerateTemplateType

    protected readonly overviewTemplateTypeOptions: {value: FabricOverviewGenerateTemplateType | "NONE"; label: string}[] = [
        {value: "NONE", label: "None"},
        {value: FabricOverviewGenerateTemplateType.FullWidthWide, label: "Full Width (Wide)"},
        {value: FabricOverviewGenerateTemplateType.FullWidthSlim, label: "Full Width (Slim)"},
        {value: FabricOverviewGenerateTemplateType.FullWidthSlimWithRepeat, label: "Full Width (Slim with Repeat)"},
    ]

    protected readonly cmToInchConvert = {
        toDisplay: (cm: number) => cm / 2.54,
        fromDisplay: (inches: number) => inches * 2.54,
    }
}
